import React from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import KpiRow from '../shared/KpiRow';
import KpiTile from '../shared/KpiTile';
import ChartPanel from '../shared/ChartPanel';
import { usePacingAnalysis } from '../../../hooks/analysis/usePacingAnalysis';
import { AXIS_PROPS, CHART_INK, PRIOR_SERIES_COLOR, SERIES_COLORS } from '../shared/chartTheme';

/**
 * Booking pace uses a fixed forward-looking window (today + 12 weeks vs the
 * same weeks last year), so there is no date-range picker here.
 */
const PacingAnalysis: React.FC = () => {
  const data = usePacingAnalysis();

  return (
    <div>
      <KpiRow>
        <KpiTile
          label="On the books — next 4 weeks"
          value={`${data.next4This} site-nights`}
          delta={data.next4Delta}
          note="vs same time last year"
        />
        <KpiTile
          label={`On the books — next ${data.horizonWeeks} weeks`}
          value={`${data.horizonThis} site-nights`}
          delta={data.horizonDelta}
          note="vs same time last year"
        />
        <KpiTile label="Last year at this point" value={`${data.horizonLast} site-nights`} />
      </KpiRow>

      <ChartPanel
        title="Booking pace"
        subtitle={`Site-nights on the books per upcoming week, vs the same weeks last year as of a year ago`}
        loading={data.loading}
        error={data.error}
        empty={data.curve.every(p => p.thisYear === 0 && p.lastYear === 0)}
        footnote="Approximate: on-the-books history is reconstructed from booking creation dates on current reservations. Modified bookings reflect their current shape, not their state at the time."
        table={{
          columns: [
            { key: 'label', label: 'Week of' },
            { key: 'thisYear', label: 'This year', align: 'right' },
            { key: 'lastYear', label: 'Last year', align: 'right' },
          ],
          rows: data.curve,
        }}
        csv={{
          filename: 'booking-pace',
          columns: [
            { key: 'weekStart', label: 'Week of' },
            { key: 'thisYear', label: 'This year OTB site-nights' },
            { key: 'lastYear', label: 'Last year OTB site-nights' },
          ],
          rows: data.curve,
        }}
      >
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={data.curve} margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
            <CartesianGrid stroke={CHART_INK.grid} vertical={false} />
            <XAxis dataKey="label" {...AXIS_PROPS} minTickGap={24} />
            <YAxis {...AXIS_PROPS} allowDecimals={false} width={48} />
            <Tooltip />
            <Legend />
            <Line
              type="monotone"
              dataKey="thisYear"
              name="This year"
              stroke={SERIES_COLORS[0]}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
            />
            <Line
              type="monotone"
              dataKey="lastYear"
              name="Last year"
              stroke={PRIOR_SERIES_COLOR}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </ChartPanel>
    </div>
  );
};

export default PacingAnalysis;
